'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import { AlertOctagon, RefreshCw, Home } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useNeumorphicTheme } from '@/hooks/useNeumorphicTheme';
const translations = {
  en: {
    system: "SYSTEM",
    error: "ERROR",
    desc: "An unexpected exception has occurred in the application layer.",
    tryAgain: "Try Again",
    returnHome: "Return Home"
  },
  ar: {
    system: "نظام",
    error: "خطأ",
    desc: "حدث استثناء غير متوقع في طبقة التطبيق.",
    tryAgain: "حاول مرة أخرى",
    returnHome: "العودة للرئيسية"
  }
};
export default function Error(t0) {
  const $ = _c(40);
  const {
    error,
    unstable_retry
  } = t0;
  const isNeumorphic = useNeumorphicTheme();
  const pathname = usePathname();
  const locale = pathname.split("/")[1] === "ar" ? "ar" : "en";
  const t = translations[locale];
  let t1;
  let t2;
  if ($[0] !== error) {
    t1 = () => {
      console.error(error);
    };
    t2 = [error];
    $[0] = error;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  const t3 = `min-h-screen flex items-center justify-center p-6 transition-all duration-300 ${isNeumorphic ? "bg-[#e0e5ec] text-[#4b5563]" : "bg-white text-black"}`;
  const t4 = `w-full max-w-xl p-8 md:p-12 transition-all duration-300 ${isNeumorphic ? "bg-[#e0e5ec] rounded-3xl shadow-[9px_9px_16px_rgba(163,177,198,0.6),_-9px_-9px_16px_rgba(255,255,255,0.5)] border border-transparent" : "border-8 border-black bg-white shadow-[8px_8px_0px_0px_#000000]"}`;
  const t5 = `inline-flex p-4 mb-6 transition-all duration-300 ${isNeumorphic ? "bg-[#e0e5ec] rounded-2xl shadow-[inset_3px_3px_6px_rgba(163,177,198,0.5),_inset_-3px_-3px_6px_rgba(255,255,255,0.7)] text-red-500/80" : "bg-black text-white border-4 border-black"}`;
  let t6;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <AlertOctagon size={48} />;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  let t7;
  if ($[4] !== t5) {
    t7 = <div className={t5}>{t6}</div>;
    $[4] = t5;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <br />;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  const t9 = `px-2 inline-block transform -skew-x-2 mt-1 ${isNeumorphic ? "bg-red-500/10 text-red-500" : "bg-black text-white"}`;
  let t10;
  if ($[7] !== t.error || $[8] !== t9) {
    t10 = <span className={t9}>{t.error}</span>;
    $[7] = t.error;
    $[8] = t9;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] !== t.system || $[11] !== t10) {
    t11 = <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tight leading-none mb-4">{t.system} {t8}{t10}</h1>;
    $[10] = t.system;
    $[11] = t10;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  const t12 = `text-lg md:text-xl font-bold uppercase mb-8 transition-colors ${isNeumorphic ? "text-zinc-600" : "text-zinc-500"}`;
  let t13;
  if ($[13] !== t.desc || $[14] !== t12) {
    t13 = <p className={t12}>{t.desc}</p>;
    $[13] = t.desc;
    $[14] = t12;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] !== unstable_retry) {
    t14 = () => unstable_retry();
    $[16] = unstable_retry;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  const t15 = `flex items-center justify-center gap-2 px-6 py-4 text-lg font-black uppercase transition-all duration-300 cursor-pointer ${isNeumorphic ? "bg-[#e0e5ec] text-[#1e293b] rounded-xl shadow-[4px_4px_8px_rgba(163,177,198,0.6),_-4px_-4px_8px_rgba(255,255,255,0.5)] hover:bg-[#d1d9e6] hover:shadow-[6px_6px_12px_rgba(163,177,198,0.7),_-6px_-6px_12px_rgba(255,255,255,0.6)] active:shadow-[inset_3px_3px_6px_rgba(163,177,198,0.6),_inset_-3px_-3px_6px_rgba(255,255,255,0.5)] active:translate-y-[2px]" : "bg-black text-white border-4 border-black hover:bg-white hover:text-black"}`;
  let t16;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <RefreshCw size={20} />;
    $[18] = t16;
  } else {
    t16 = $[18];
  }
  let t17;
  if ($[19] !== t.tryAgain || $[20] !== t14 || $[21] !== t15) {
    t17 = <button onClick={t14} className={t15}>{t16} {t.tryAgain}</button>;
    $[19] = t.tryAgain;
    $[20] = t14;
    $[21] = t15;
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  const t18 = `/${locale}`;
  const t19 = `flex items-center justify-center gap-2 px-6 py-4 text-lg font-black uppercase transition-all duration-300 ${isNeumorphic ? "bg-[#e0e5ec] text-[#4b5563] rounded-xl shadow-[4px_4px_8px_rgba(163,177,198,0.6),_-4px_-4px_8px_rgba(255,255,255,0.5)] hover:bg-[#d1d9e6] hover:shadow-[6px_6px_12px_rgba(163,177,198,0.7),_-6px_-6px_12px_rgba(255,255,255,0.6)] active:shadow-[inset_3px_3px_6px_rgba(163,177,198,0.6),_inset_-3px_-3px_6px_rgba(255,255,255,0.5)] active:translate-y-[2px]" : "bg-white text-black border-4 border-black hover:bg-black hover:text-white"}`;
  let t20;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <Home size={20} />;
    $[23] = t20;
  } else {
    t20 = $[23];
  }
  let t21;
  if ($[24] !== t.returnHome || $[25] !== t18 || $[26] !== t19) {
    t21 = <Link href={t18} className={t19}>{t20} {t.returnHome}</Link>;
    $[24] = t.returnHome;
    $[25] = t18;
    $[26] = t19;
    $[27] = t21;
  } else {
    t21 = $[27];
  }
  let t22;
  if ($[28] !== t17 || $[29] !== t21) {
    t22 = <div className="flex flex-col sm:flex-row gap-4">{t17}{t21}</div>;
    $[28] = t17;
    $[29] = t21;
    $[30] = t22;
  } else {
    t22 = $[30];
  }
  let t23;
  if ($[31] !== t11 || $[32] !== t13 || $[33] !== t22 || $[34] !== t4 || $[35] !== t7) {
    t23 = <div className={t4}>{t7}{t11}{t13}{t22}</div>;
    $[31] = t11;
    $[32] = t13;
    $[33] = t22;
    $[34] = t4;
    $[35] = t7;
    $[36] = t23;
  } else {
    t23 = $[36];
  }
  let t24;
  if ($[37] !== t23 || $[38] !== t3) {
    t24 = <div className={t3}>{t23}</div>;
    $[37] = t23;
    $[38] = t3;
    $[39] = t24;
  } else {
    t24 = $[39];
  }
  return t24;
}